/**
 * What the Reports screen says while a PDF is built and downloaded or emailed:
 * "Preparing report…", then "Report downloaded" / "Report emailed", or a
 * clear error that says what to do next.
 */

import { FOOD_SAFETY_PERMISSION_DENIED_CODE } from 'src/constants/foodSafety'

export const FOOD_SAFETY_REPORT_EXPORT_MESSAGE_KEY = 'food-safety-report-export'

export const FOOD_SAFETY_REPORT_EXPORT_KIND = Object.freeze({
  DOWNLOAD: 'download',
  EMAIL: 'email',
})

export const FOOD_SAFETY_REPORT_PREPARING = 'Preparing report…'
export const FOOD_SAFETY_REPORT_DOWNLOADED = 'Report downloaded'
export const FOOD_SAFETY_REPORT_EMAILED = 'Report emailed'
export const FOOD_SAFETY_REPORT_EMPTY_PDF_CODE = 'FOOD_SAFETY_EMPTY_PDF'

const isEmail = kind => kind === FOOD_SAFETY_REPORT_EXPORT_KIND.EMAIL

const sentToLine = data => {
  const sentTo = Array.isArray(data?.sentTo)
    ? data.sentTo.filter(Boolean).join(', ')
    : String(data?.sentTo || '').trim()
  return sentTo || null
}

export function reportExportPreparingStatus(kind) {
  return {
    state: 'preparing',
    kind,
    title: FOOD_SAFETY_REPORT_PREPARING,
    detail: isEmail(kind)
      ? 'Building the PDF and sending it — this can take a few seconds.'
      : 'Building the PDF — this can take a few seconds.',
  }
}

export function reportDownloadedStatus({ filename, sample = false } = {}) {
  const name = String(filename || '').trim()
  return {
    state: 'done',
    kind: FOOD_SAFETY_REPORT_EXPORT_KIND.DOWNLOAD,
    title: FOOD_SAFETY_REPORT_DOWNLOADED,
    detail: sample
      ? ['Sample (demo kitchen)', name].filter(Boolean).join(' · ')
      : name || null,
  }
}

export function reportEmailedStatus({ data, sample = false } = {}) {
  const sentTo = sentToLine(data)
  return {
    state: 'done',
    kind: FOOD_SAFETY_REPORT_EXPORT_KIND.EMAIL,
    title: FOOD_SAFETY_REPORT_EMAILED,
    detail:
      [
        sample ? 'Sample (demo kitchen)' : null,
        sentTo ? `Sent to ${sentTo} — check your inbox` : null,
      ]
        .filter(Boolean)
        .join(' · ') || null,
  }
}

/** A zero-byte PDF is a failure, not a download. */
export function assertReportPdf(blob) {
  if (blob && Number(blob.size) === 0) {
    const err = new Error('The report came back empty.')
    err.code = FOOD_SAFETY_REPORT_EMPTY_PDF_CODE
    throw err
  }
  return blob
}

const isNetworkError = error =>
  !error?.statusCode &&
  !error?.response &&
  /network\s*error|failed to fetch|load failed/i.test(error?.message || '')

const isTimeout = error =>
  error?.code === 'ECONNABORTED' ||
  error?.statusCode === 504 ||
  /timeout|timed out/i.test(error?.message || '')

const isEmailSetupProblem = error =>
  error?.statusCode === 400 ||
  /manager report email|account has an email/i.test(error?.message || '')

/**
 * Turns a failed download / email into "what went wrong" + "what to do".
 * `state: 'warning'` = fix something first (no retry); `'error'` = try again.
 */
export function reportExportErrorStatus(error, kind) {
  const title = isEmail(kind)
    ? 'Couldn’t email the report'
    : 'Couldn’t download the report'
  const base = { kind, title }
  const serverMessage = String(error?.message || '').trim()

  if (error?.code === FOOD_SAFETY_PERMISSION_DENIED_CODE) {
    return {
      ...base,
      state: 'warning',
      detail: serverMessage || 'Your role here can’t export reports.',
      canRetry: false,
    }
  }
  if (isEmail(kind) && isEmailSetupProblem(error)) {
    return {
      ...base,
      state: 'warning',
      detail: serverMessage || 'Add a report email in setup first.',
      canRetry: false,
    }
  }
  if (error?.code === FOOD_SAFETY_REPORT_EMPTY_PDF_CODE) {
    return {
      ...base,
      state: 'error',
      detail: 'The report came back empty. Try again.',
      canRetry: true,
    }
  }
  if (isNetworkError(error)) {
    return {
      ...base,
      state: 'error',
      detail: 'Couldn’t reach OMS. Check your connection and try again.',
      canRetry: true,
    }
  }
  if (isTimeout(error)) {
    return {
      ...base,
      state: 'error',
      detail: 'The report took too long to prepare. Try again in a minute.',
      canRetry: true,
    }
  }
  if (Number(error?.statusCode) >= 500) {
    return {
      ...base,
      state: 'error',
      detail:
        'OMS couldn’t prepare the report just now. Try again in a minute.',
      canRetry: true,
    }
  }
  return {
    ...base,
    state: 'error',
    detail: serverMessage
      ? `${serverMessage.replace(/\.$/, '')}. Try again.`
      : 'Something went wrong. Try again.',
    canRetry: true,
  }
}

/** One line for the toast: "Report downloaded — Food_Safety_….pdf". */
export function formatReportExportToast(status) {
  if (!status?.title) return ''
  return status.detail ? `${status.title} — ${status.detail}` : status.title
}
